import { useState, useEffect, useCallback } from "react";

// Evaluations come from our own DB now (employee_evaluations via
// /api/evaluations). REACT_APP_API_BASE_URL already ends in /api.
const API = process.env.REACT_APP_API_BASE_URL;

// ─── DESIGN TOKENS (NOVAA) ───────────────────────────────────────
// Everything is driven off the global --nd-* theme variables (see
// src/styles/theme.css) so this page tracks the app-wide light/dark
// toggle for free. Brand accents stay as hex — they're theme-neutral.
const T = {
  bg:         'var(--nd-bg)',
  surface:    'var(--nd-surface)',
  border:     'var(--nd-w06)',
  border2:    'var(--nd-w10)',
  text:       'var(--nd-text)',
  textStrong: 'var(--nd-text-strong)',
  textMuted:  'var(--nd-tx35)',
  faint:      'var(--nd-tx22)',
  blue:       '#3B82F6',
  blueLight:  '#60A5FA',
  green:      '#22D3A5',
  amber:      '#F59E0B',
  red:        '#F87171',
  purple:     '#A78BFA',
};

const GLOBAL = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .nv-eval * { box-sizing: border-box; }
  .nv-eval ::-webkit-scrollbar { width: 7px; height: 7px; }
  .nv-eval ::-webkit-scrollbar-thumb { background: var(--nd-w08); border-radius: 4px; }
  .nv-eval ::-webkit-scrollbar-track { background: transparent; }
  .nv-eval input {
    background: var(--nd-w05); border: 1px solid var(--nd-w10); border-radius: 8px;
    color: var(--nd-text); font-family: 'DM Sans', sans-serif; font-size: 13px;
    padding: 9px 13px; outline: none; width: 100%;
  }
  .nv-eval input:focus { border-color: rgba(96,165,250,0.5); }
  .nv-eval input::placeholder { color: var(--nd-tx35); }
  @keyframes nv-eval-spin { to { transform: rotate(360deg); } }
  @keyframes nv-eval-pop { from { transform: translateY(8px) scale(.99); opacity: 0; } to { transform: none; opacity: 1; } }
  .nv-eval .nv-hoverable { transition: transform .15s, border-color .15s, box-shadow .15s; }
  .nv-eval .nv-hoverable:hover { transform: translateY(-2px); border-color: rgba(96,165,250,0.5); box-shadow: var(--nd-shadow); }
`;

const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14 };
const grid210 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 14 };
const grid250 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 14 };

// The API returns raw column names; the cards below were written against the
// old Google Sheet's field names. Translate once, on the way in.
const fromApi = (r) => ({
  name:       r.employee_name,
  id:         r.employee_id,
  role:       r.role,
  site:       r.site,
  supervisor: r.supervisor_name,
  date:       r.created_at ? new Date(r.created_at).toLocaleDateString() : "",
  remarks:    r.supervisor_remarks,
  ehs:        r.ehs_violation,
  s2:         r.attendance_score,
  s3:         r.productivity_score,
  s4:         r.reporting_score,
  s5:         r.problem_solving_score,
  s6:         r.ehs_score,
  s7:         r.supervisor_score,
  total:      r.total_score,
  outcome:    r.outcome,
  evaluationId: r.id,

  // Raw answers the mobile app stores. It posts the individual answers plus a
  // final total but NOT the per-section subtotals, so the section columns above
  // arrive NULL. Carrying these through lets the dashboard recompute the
  // breakdown (see deriveSections) and replay the fully filled form.
  attendanceStatus: r.attendance_status,
  panelPct:         r.panel_pct,
  workQuality:      r.work_quality,
  taskReliability:  r.task_reliability,
  commClarity:      r.comm_clarity,
  problemReporting: r.problem_reporting,
  handlesIssues:    r.handles_issues,
  takesInitiative:  r.takes_initiative,
  ppeCompliance:    r.ppe_compliance,
  safetyHandling:   r.safety_handling,
  safetyTraining:   r.safety_training,

  // Signature images only come back from GET /:id; the list endpoint returns
  // has_* booleans so we know whether a detail fetch is worthwhile.
  supSig:    r.supervisor_signature,
  empSig:    r.employee_signature,
  hasSupSig: r.has_supervisor_signature,
  hasEmpSig: r.has_employee_signature,
});

const AVATAR_COLORS = ['#3B82F6', '#22D3A5', '#A78BFA', '#F59E0B', '#60A5FA', '#F9A8D4'];
const avatarColor = (n) => AVATAR_COLORS[(n || "A").charCodeAt(0) % AVATAR_COLORS.length];
const initials = (n) => (n || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

// Score-tier colour used for averages across the page.
const scoreTone = (v) => (v >= 90 ? T.green : v >= 80 ? T.blueLight : T.amber);

// Scores from BOTH possible field name formats
const getScore = (r, field, altField) => {
  const v = r[field] ?? r[altField] ?? 0;
  return Number(v) || 0;
};

// Stored per-section columns — either the dashboard's s2… aliases or the web
// form's camelCase names.
const SECTION_KEYS = [
  ["s2", "attendanceScore"], ["s3", "productivityScore"], ["s4", "reportingScore"],
  ["s5", "problemSolvingScore"], ["s6", "ehsScore"],
];
const hasStoredSections = (r) =>
  SECTION_KEYS.some(([a, b]) => r[a] != null || r[b] != null);

// Raw answers the mobile app posts (answers + total, no per-section subtotals).
const RAW_ANSWER_KEYS = [
  "attendanceStatus", "panelPct", "workQuality", "taskReliability", "commClarity",
  "problemReporting", "handlesIssues", "takesInitiative", "ppeCompliance",
  "safetyHandling", "safetyTraining",
];
const hasRawAnswers = (r) =>
  RAW_ANSWER_KEYS.some((k) => r[k] != null && r[k] !== "");

// Recompute the six section scores from the raw answers, mirroring the scoring
// in EmployeeEvaluation.js exactly — a mobile-posted record then shows the same
// breakdown the web form would have produced (verified to reproduce every
// stored total_score).
const deriveSections = (r) => {
  const yes = (v) => v === "yes";
  const s2 = r.attendanceStatus === "pass" ? 25 : 0;
  let s3 = 0;
  const pct = parseFloat(r.panelPct);
  if (!isNaN(pct)) {
    if (pct >= 100) s3 += 12;
    else if (pct >= 80) s3 += 10;
    else if (pct >= 60) s3 += 7;
    else s3 += 3;
  }
  if (r.workQuality === "good") s3 += 7;
  if (yes(r.taskReliability)) s3 += 6;
  s3 = Math.min(25, s3);
  const s4 = (yes(r.commClarity) ? 13 : 0) + (yes(r.problemReporting) ? 12 : 0);
  const s5 = (yes(r.handlesIssues) ? 5 : 0) + (yes(r.takesInitiative) ? 5 : 0);
  const ehs = r.ehs || r.ehsViolation || "none";
  const s6 = (ehs === "major" || ehs === "complaints2")
    ? 0
    : (yes(r.ppeCompliance) ? 4 : 0) + (yes(r.safetyHandling) ? 3 : 0) + (yes(r.safetyTraining) ? 3 : 0);
  const s7 = Math.min(5, Math.max(0, Number(r.s7 ?? r.supervisorScore) || 0));
  return { s2, s3, s4, s5, s6, s7 };
};

// Single source of truth for a record's six section scores: prefer the stored
// columns, recompute from raw answers when they're NULL (mobile submissions),
// and only give up (known:false) when neither exists — the card then shows the
// stored total with dashes for the breakdown.
const resolveSections = (r) => {
  if (hasStoredSections(r)) {
    return {
      known: true,
      s2: getScore(r, "s2", "attendanceScore"),
      s3: getScore(r, "s3", "productivityScore"),
      s4: getScore(r, "s4", "reportingScore"),
      s5: getScore(r, "s5", "problemSolvingScore"),
      s6: getScore(r, "s6", "ehsScore"),
      s7: getScore(r, "s7", "supervisorScore"),
    };
  }
  if (hasRawAnswers(r)) return { known: true, ...deriveSections(r) };
  return { known: false, s2: 0, s3: 0, s4: 0, s5: 0, s6: 0, s7: 0 };
};

const calcTotal = (r) => {
  const sec = resolveSections(r);
  if (!sec.known) return Number(r.total ?? r.totalScore ?? 0) || 0;
  return sec.s2 + sec.s3 + sec.s4 + sec.s5 + sec.s6 + sec.s7;
};

function calcOutcome(r) {
  const t = calcTotal(r);
  if (r.ehs === "major")       return { label: "Immediate Action", tone: T.red,       soft: 'var(--nd-red-soft)',   strong: 'var(--nd-red-strong)',   stars: 0 };
  if (r.ehs === "complaints2") return { label: "Promo Blocked",    tone: T.amber,     soft: 'var(--nd-amber-soft)', strong: 'var(--nd-amber-strong)', stars: 0 };
  if (t >= 90)                 return { label: "Excellent",        tone: T.green,     soft: 'var(--nd-green-soft)', strong: 'var(--nd-green-strong)', stars: 3 };
  if (t >= 80)                 return { label: "Good",             tone: T.blueLight, soft: 'var(--nd-blue-soft)',  strong: 'var(--nd-blue-strong)',  stars: 2 };
  return                       { label: "Average",                 tone: T.amber,     soft: 'var(--nd-amber-soft)', strong: 'var(--nd-amber-strong)', stars: 1 };
}

const EHS_LABELS = {
  none: "No Violation",
  minor1: "1 Minor (allowed)",
  complaints2: "2 Complaints (promo blocked)",
  major: "Major (immediate action)",
};
const ehsTone = (ehs) =>
  ehs === "major" ? { c: T.red, bg: "var(--nd-red-soft)" }
  : (ehs === "complaints2" || ehs === "minor1") ? { c: T.amber, bg: "var(--nd-amber-soft)" }
  : { c: T.green, bg: "var(--nd-green-soft)" };

function Spinner({ msg }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ width: 34, height: 34, border: `3px solid var(--nd-w10)`, borderTopColor: T.blue, borderRadius: "50%", margin: "0 auto 14px", animation: "nv-eval-spin .8s linear infinite" }} />
      <div style={{ fontSize: 12.5, color: T.textMuted }}>{msg}</div>
    </div>
  );
}

function ErrorBox({ message, onRetry }) {
  return (
    <div style={{ background: 'var(--nd-red-soft)', border: "1px solid rgba(248,113,113,0.35)", borderRadius: 12, padding: 20, textAlign: "center", margin: "12px 0" }}>
      <div style={{ fontSize: 13, color: T.red, fontWeight: 700, marginBottom: 8 }}>Couldn't load evaluations</div>
      <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 14, whiteSpace: "pre-wrap" }}>{message}</div>
      <button onClick={onRetry} style={{ background: T.blue, color: "#fff", border: "none", borderRadius: 8, padding: "8px 20px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
        Try again
      </button>
    </div>
  );
}

function EmptyState({ title, sub }) {
  return (
    <div style={{ textAlign: "center", padding: "56px 20px", color: T.textMuted }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>{title}</div>
      {sub && <div style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 800, color: T.textMuted, textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 12 }}>
      {children}
    </div>
  );
}

function BackButton({ onClick, label }) {
  return (
    <button onClick={onClick}
      style={{ background: "var(--nd-w05)", border: `1px solid ${T.border2}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer", color: T.text, fontWeight: 600, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" }}>
      ← {label}
    </button>
  );
}

function Banner({ name, sub }) {
  return (
    <div style={{ ...card, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 14, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 3, background: "linear-gradient(180deg,#3B82F6,#22D3A5)" }} />
      <Avatar name={name} size={44} fsize={15} />
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: T.textStrong }}>{name}</div>
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{sub}</div>
      </div>
    </div>
  );
}

function StatCard({ value, label, tone, loading }) {
  return (
    <div style={{ ...card, padding: "16px 18px" }}>
      <div style={{ fontSize: 30, fontWeight: 800, fontFamily: "'DM Mono', monospace", lineHeight: 1, color: loading ? T.textMuted : (tone || T.textStrong) }}>
        {loading ? "—" : value}
      </div>
      <div style={{ fontSize: 8.5, color: T.textMuted, letterSpacing: "0.14em", marginTop: 8, fontWeight: 700, textTransform: "uppercase" }}>{label}</div>
    </div>
  );
}

function Avatar({ name, size = 42, fsize = 13 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: avatarColor(name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: fsize, flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}

function SupervisorCard({ sup, onClick }) {
  const tone = scoreTone(sup.avg);
  return (
    <div className="nv-hoverable" onClick={onClick}
      style={{ ...card, padding: 16, cursor: "pointer", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
      <div style={{ marginTop: 6, marginBottom: 10 }}><Avatar name={sup.name} size={44} fsize={15} /></div>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.textStrong, marginBottom: 3 }}>{sup.name}</div>
      <div style={{ fontSize: 9, fontWeight: 700, color: T.textMuted, letterSpacing: "0.14em", marginBottom: 12 }}>SUPERVISOR</div>
      <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${T.border}`, paddingTop: 10, fontSize: 11, color: T.textMuted }}>
        <span>{sup.employees} employees</span>
        <span>{sup.forms} forms</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 8 }}>
        <span style={{ color: tone, fontWeight: 700, fontSize: 12, fontFamily: "'DM Mono', monospace" }}>Avg {sup.avg}/100</span>
        <span style={{ fontSize: 10.5, color: T.blueLight, fontWeight: 700 }}>View →</span>
      </div>
    </div>
  );
}

function EmployeeCard({ emp, onClick }) {
  const tone = scoreTone(emp.avg);
  return (
    <div className="nv-hoverable" onClick={onClick}
      style={{ ...card, padding: 14, cursor: "pointer" }}>
      <div style={{ marginBottom: 8 }}><Avatar name={emp.name} size={40} fsize={13} /></div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{emp.name}</div>
      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>{emp.role || "—"} · {emp.site || "—"}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${T.border}`, paddingTop: 8, fontSize: 11 }}>
        <span style={{ color: T.textMuted }}>{emp.forms} form{emp.forms === 1 ? "" : "s"}</span>
        <span style={{ color: tone, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>Avg {emp.avg}</span>
      </div>
    </div>
  );
}

function FormCard({ record, onOpen }) {
  const sec = resolveSections(record);
  const { s2, s3, s4, s5, s6, s7 } = sec;
  const breakdown = sec.known;
  const t  = calcTotal(record);
  const oc = calcOutcome(record);
  const ehs = record.ehs || record.ehsViolation || "none";
  const ehsLabel = ehs === "none" ? "No Violation" : ehs === "minor1" ? "Minor" : ehs === "complaints2" ? "2 Complaints" : "Major";
  const eTone =
    ehs === "major" ? { c: T.red, bg: "var(--nd-red-soft)" }
    : (ehs === "complaints2" || ehs === "minor1") ? { c: T.amber, bg: "var(--nd-amber-soft)" }
    : { c: T.textMuted, bg: "var(--nd-w05)" };

  const sections = [
    { label: "Attendance",   val: s2, max: 25 },
    { label: "Productivity", val: s3, max: 25 },
    { label: "Reporting",    val: s4, max: 25 },
    { label: "Problem",      val: s5, max: 10 },
    { label: "EHS",          val: s6, max: 10 },
    { label: "Supervisor",   val: s7, max: 5  },
  ];

  return (
    <div className="nv-hoverable" onClick={onOpen} style={{ ...card, overflow: "hidden", cursor: "pointer" }}>
      <div style={{ padding: "11px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.textStrong }}>Week ending {record.date || "—"}</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{record.site || "—"} · {record.role || "—"}</div>
        </div>
        <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em", color: eTone.c, background: eTone.bg, padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap", textTransform: "uppercase" }}>
          {ehsLabel}
        </span>
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 38, fontWeight: 800, fontFamily: "'DM Mono', monospace", color: oc.tone, lineHeight: 1 }}>{t}</span>
          <span style={{ fontSize: 15, color: T.textMuted, fontWeight: 500 }}>/100</span>
        </div>
        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span style={{ display: "inline-block", background: oc.soft, color: oc.strong, fontWeight: 700, fontSize: 11, padding: "4px 12px", borderRadius: 999 }}>
            {oc.label}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
          {sections.map(s => (
            <div key={s.label} style={{ background: "var(--nd-w04)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 4px", textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: breakdown ? T.text : T.textMuted }}>
                {breakdown ? s.val : "—"}<span style={{ fontSize: 9, color: T.textMuted, fontWeight: 400 }}>/{s.max}</span>
              </div>
              <div style={{ fontSize: 8.5, color: T.textMuted, marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
        {!breakdown && (
          <div style={{ marginTop: 8, fontSize: 9.5, color: T.faint, textAlign: "center", fontStyle: "italic" }}>
            Section breakdown not recorded — total only.
          </div>
        )}
        {record.remarks && (
          <div style={{ marginTop: 12, fontSize: 11, color: T.textMuted, borderTop: `1px solid ${T.border}`, paddingTop: 10, fontStyle: "italic" }}>
            "{record.remarks}"
          </div>
        )}
        {oc.stars > 0 && (
          <div style={{ textAlign: "center", marginTop: 10, fontSize: 14, letterSpacing: 3 }}>
            <span style={{ color: oc.tone }}>{"★".repeat(oc.stars)}</span>
            <span style={{ color: "var(--nd-w12)" }}>{"★".repeat(3 - oc.stars)}</span>
          </div>
        )}
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}`, textAlign: "center", fontSize: 10.5, color: T.blueLight, fontWeight: 700 }}>
          View filled form →
        </div>
      </div>
    </div>
  );
}

// ─── Filled-form replay ──────────────────────────────────────────
function Plain({ value }) {
  if (value == null || value === "") return <span style={{ fontSize: 12, color: T.faint }}>—</span>;
  return <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{value}</span>;
}

const ANSWER_MAP = {
  yes:  { label: "Yes ✓",     tone: T.green, soft: 'var(--nd-green-soft)' },
  no:   { label: "No ✗",      tone: T.red,   soft: 'var(--nd-red-soft)' },
  pass: { label: "Present ✓", tone: T.green, soft: 'var(--nd-green-soft)' },
  fail: { label: "Absent ✗",  tone: T.red,   soft: 'var(--nd-red-soft)' },
  good: { label: "Good",      tone: T.green, soft: 'var(--nd-green-soft)' },
  bad:  { label: "Bad",       tone: T.red,   soft: 'var(--nd-red-soft)' },
};

function Answer({ value }) {
  const a = ANSWER_MAP[(value ?? "").toString().toLowerCase()];
  if (!a) return <Plain value={value} />;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: a.tone, background: a.soft, padding: "3px 10px", borderRadius: 999 }}>
      {a.label}
    </span>
  );
}

function CriteriaLine({ label, children, last }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "9px 0", borderBottom: last ? "none" : `1px solid ${T.border}` }}>
      <span style={{ fontSize: 12, color: T.text }}>{label}</span>
      <span style={{ flexShrink: 0, textAlign: "right" }}>{children}</span>
    </div>
  );
}

function ModalSection({ title, score, max, children }) {
  return (
    <div style={{ ...card, padding: "10px 14px 4px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 4, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.1em", color: T.textMuted, textTransform: "uppercase" }}>{title}</div>
        {score != null && (
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'DM Mono', monospace", color: T.text }}>
            {score}<span style={{ fontSize: 10, color: T.textMuted }}>/{max}</span>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function FilledFormModal({ record, onClose }) {
  // Start from the list record — it already has every answer. The (large)
  // signature image is the only thing missing, so pull the full record only
  // when the list told us a signature exists.
  const [full, setFull] = useState(record);
  const [sigLoading, setSigLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const needSig = record.hasSupSig || record.hasEmpSig;
    if (!needSig || !record.evaluationId || !API) return undefined;
    setSigLoading(true);
    (async () => {
      try {
        const res  = await fetch(`${API}/evaluations/${record.evaluationId}`);
        const json = await res.json().catch(() => ({}));
        if (alive && res.ok && json.success && json.evaluation) {
          setFull({ ...record, ...fromApi(json.evaluation) });
        }
      } catch {
        /* keep the list record — signatures simply won't render */
      } finally {
        if (alive) setSigLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [record]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sec = resolveSections(full);
  const t   = calcTotal(full);
  const oc  = calcOutcome(full);
  const ehs = full.ehs || full.ehsViolation || "none";
  const eTone = ehsTone(ehs);
  const sc = (v) => (sec.known ? v : null);

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(2,6,20,0.62)", backdropFilter: "blur(3px)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "5vh 14px", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ ...card, width: "100%", maxWidth: 560, overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,0.5)", animation: "nv-eval-pop .18s ease-out" }}>

        {/* Header */}
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, position: "relative" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Avatar name={full.name} size={42} fsize={14} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.textStrong }}>{full.name || "—"}</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                Week ending {full.date || "—"} · {full.supervisor || "—"}
              </div>
            </div>
          </div>
          <button onClick={onClose} aria-label="Close"
            style={{ background: "var(--nd-w05)", border: `1px solid ${T.border2}`, borderRadius: 8, width: 30, height: 30, cursor: "pointer", color: T.text, fontSize: 17, lineHeight: 1, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>
            ×
          </button>
        </div>

        <div style={{ padding: 16, maxHeight: "72vh", overflowY: "auto" }}>
          {/* Score summary */}
          <div style={{ ...card, padding: 14, marginBottom: 12, textAlign: "center" }}>
            <span style={{ fontSize: 40, fontWeight: 800, fontFamily: "'DM Mono', monospace", color: oc.tone, lineHeight: 1 }}>{t}</span>
            <span style={{ fontSize: 16, color: T.textMuted }}>/100</span>
            <div style={{ marginTop: 8 }}>
              <span style={{ display: "inline-block", background: oc.soft, color: oc.strong, fontWeight: 700, fontSize: 11, padding: "4px 14px", borderRadius: 999 }}>{oc.label}</span>
            </div>
            {oc.stars > 0 && (
              <div style={{ marginTop: 8, fontSize: 15, letterSpacing: 3 }}>
                <span style={{ color: oc.tone }}>{"★".repeat(oc.stars)}</span>
                <span style={{ color: "var(--nd-w12)" }}>{"★".repeat(3 - oc.stars)}</span>
              </div>
            )}
            {!sec.known && (
              <div style={{ marginTop: 8, fontSize: 10, color: T.faint, fontStyle: "italic" }}>
                Section breakdown not recorded for this record — total only.
              </div>
            )}
          </div>

          <ModalSection title="Basic Information">
            <CriteriaLine label="Employee ID"><Plain value={full.id} /></CriteriaLine>
            <CriteriaLine label="Role"><Plain value={full.role} /></CriteriaLine>
            <CriteriaLine label="Site"><Plain value={full.site} /></CriteriaLine>
            <CriteriaLine label="Supervisor" last><Plain value={full.supervisor} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 2 · Attendance" score={sc(sec.s2)} max={25}>
            <CriteriaLine label="≥ 90% attendance (weekly)" last><Answer value={full.attendanceStatus} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 3 · Productivity" score={sc(sec.s3)} max={25}>
            <CriteriaLine label="Panel cleaning productivity"><Plain value={full.panelPct != null && full.panelPct !== "" ? `${full.panelPct}%` : null} /></CriteriaLine>
            <CriteriaLine label="Work quality"><Answer value={full.workQuality} /></CriteriaLine>
            <CriteriaLine label="Task reliability" last><Answer value={full.taskReliability} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 4 · Reporting & Communication" score={sc(sec.s4)} max={25}>
            <CriteriaLine label="Communication clarity"><Answer value={full.commClarity} /></CriteriaLine>
            <CriteriaLine label="Problem reporting" last><Answer value={full.problemReporting} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 5 · Problem Solving & Initiative" score={sc(sec.s5)} max={10}>
            <CriteriaLine label="Handles issues independently"><Answer value={full.handlesIssues} /></CriteriaLine>
            <CriteriaLine label="Takes initiative" last><Answer value={full.takesInitiative} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 6 · EHS Compliance" score={sc(sec.s6)} max={10}>
            <CriteriaLine label="Violation status">
              <span style={{ fontSize: 10.5, fontWeight: 800, color: eTone.c, background: eTone.bg, padding: "3px 10px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {EHS_LABELS[ehs] || ehs}
              </span>
            </CriteriaLine>
            <CriteriaLine label="PPE compliance"><Answer value={full.ppeCompliance} /></CriteriaLine>
            <CriteriaLine label="Safety equipment handling"><Answer value={full.safetyHandling} /></CriteriaLine>
            <CriteriaLine label="Safety training compliance" last><Answer value={full.safetyTraining} /></CriteriaLine>
          </ModalSection>

          <ModalSection title="Section 7 · Supervisor's Feedback" score={sc(sec.s7)} max={5}>
            <CriteriaLine label="Supervisor score (0–5)" last={!full.remarks}><Plain value={full.s7 ?? full.supervisorScore} /></CriteriaLine>
            {full.remarks && (
              <div style={{ padding: "10px 0 6px", fontSize: 12, color: T.textMuted, fontStyle: "italic" }}>
                "{full.remarks}"
              </div>
            )}
          </ModalSection>

          <ModalSection title="Signatures">
            <div style={{ padding: "8px 0 10px" }}>
              {full.supSig ? (
                <>
                  <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 6 }}>Supervisor</div>
                  <div style={{ background: "#fff", borderRadius: 8, padding: 6, border: `1px solid ${T.border}` }}>
                    <img src={full.supSig} alt="Supervisor signature" style={{ display: "block", maxWidth: "100%", height: "auto" }} />
                  </div>
                </>
              ) : (record.hasSupSig && sigLoading) ? (
                <div style={{ fontSize: 12, color: T.textMuted }}>Loading signature…</div>
              ) : (
                <div style={{ fontSize: 12, color: T.faint, fontStyle: "italic" }}>Not signed.</div>
              )}
            </div>
          </ModalSection>
        </div>
      </div>
    </div>
  );
}

export default function EvaluationDashboard() {
  const [db, setDb]                = useState([]);
  const [loading, setLoading]      = useState(true);
  const [error, setError]          = useState(null);
  const [view, setView]            = useState("supervisors");
  const [currentSup, setCurrentSup] = useState(null);
  const [currentEmp, setCurrentEmp] = useState(null);
  const [search, setSearch]        = useState("");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [openForm, setOpenForm]    = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      if (!API) throw new Error("REACT_APP_API_BASE_URL is not set.");
      const res  = await fetch(`${API}/evaluations`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        throw new Error(json.message || `Server returned HTTP ${res.status}`);
      }
      const clean = (json.evaluations || [])
        .map(fromApi)
        .filter(r => r.name && r.name.toString().trim() !== "");
      setDb(clean);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Download CSV Logic
  const handleDownloadCSV = () => {
    if (!db.length) return;

    const headers = [
      "Date",
      "Supervisor",
      "Employee Name",
      "Employee ID",
      "Role",
      "Site",
      "Total Score",
      "Outcome",
      "EHS Violation",
      "Attendance Status",
      "Panel Cleaning %",
      "Work Quality",
      "Task Reliability",
      "Comm. Clarity",
      "Problem Reporting",
      "Handles Issues",
      "Takes Initiative",
      "PPE Compliance",
      "Safety Handling",
      "Safety Training",
      "Supervisor Score",
      "Supervisor Remarks"
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '""';
      return '"' + String(val).replace(/"/g, '""') + '"';
    };

    const rows = db.map(r => {
      const outcomeLabel = calcOutcome(r).label;
      const totalScore = calcTotal(r);
      const ehsLabel = EHS_LABELS[r.ehs] || r.ehs || "No Violation";

      return [
        r.date,
        r.supervisor,
        r.name,
        r.id,
        r.role,
        r.site,
        totalScore,
        outcomeLabel,
        ehsLabel,
        r.attendanceStatus,
        r.panelPct,
        r.workQuality,
        r.taskReliability,
        r.commClarity,
        r.problemReporting,
        r.handlesIssues,
        r.takesInitiative,
        r.ppeCompliance,
        r.safetyHandling,
        r.safetyTraining,
        r.s7 ?? r.supervisorScore,
        r.remarks
      ].map(escapeCSV).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Novaa_Evaluations_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getSupervisors = () => {
    const map = {};
    db.forEach(r => {
      const sup = (r.supervisor || "Unknown").toString().trim();
      if (!map[sup]) map[sup] = { name: sup, employeeSet: new Set(), forms: 0, totalScore: 0 };
      map[sup].employeeSet.add((r.name || "").toString().trim());
      map[sup].forms++;
      map[sup].totalScore += calcTotal(r);
    });
    return Object.values(map).map(s => ({ ...s, employees: s.employeeSet.size, avg: s.forms ? Math.round(s.totalScore / s.forms) : 0 }));
  };

  const getEmployees = (sup) => {
    const map = {};
    db.filter(r => (r.supervisor || "").toString().trim() === sup).forEach(r => {
      const nm = (r.name || "").toString().trim();
      if (!map[nm]) map[nm] = { name: nm, id: r.id, role: r.role, site: r.site, forms: 0, totalScore: 0 };
      map[nm].forms++;
      map[nm].totalScore += calcTotal(r);
    });
    return Object.values(map).map(e => ({ ...e, avg: e.forms ? Math.round(e.totalScore / e.forms) : 0 }));
  };

  const getForms = (sup, emp) =>
    db.filter(r => (r.supervisor || "").toString().trim() === sup && (r.name || "").toString().trim() === emp)
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""));

  const allSups = getSupervisors();
  const allEmps = [...new Set(db.map(r => (r.name || "").toString().trim()).filter(Boolean))];
  const avgScore = db.length ? Math.round(db.reduce((a, r) => a + calcTotal(r), 0) / db.length) : 0;

  const goSupervisors = () => { setView("supervisors"); setCurrentSup(null); setCurrentEmp(null); setSearch(""); };
  const goEmployees   = (sup) => { setView("employees"); setCurrentSup(sup); setCurrentEmp(null); setSearch(""); };
  const goForms       = (emp) => { setView("forms"); setCurrentEmp(emp); };

  const renderSupervisors = () => {
    if (loading) return <Spinner msg="Loading evaluations…" />;
    if (error)   return <ErrorBox message={error} onRetry={fetchData} />;
    if (!allSups.length) return <EmptyState title="No evaluations recorded yet" sub="No evaluation records have been submitted." />;
    return (
      <>
        <SectionLabel>Supervisors ({allSups.length})</SectionLabel>
        <div style={grid210}>
          {allSups.map(s => <SupervisorCard key={s.name} sup={s} onClick={() => goEmployees(s.name)} />)}
        </div>
      </>
    );
  };

  const renderEmployees = () => {
    let list = getEmployees(currentSup);
    if (search) list = list.filter(e => e.name.toLowerCase().includes(search.toLowerCase()) || (e.role || "").toLowerCase().includes(search.toLowerCase()));
    return (
      <>
        <BackButton onClick={goSupervisors} label="Back to Supervisors" />
        <Banner name={currentSup} sub={`${list.length} employee${list.length === 1 ? "" : "s"}`} />
        <div style={{ marginBottom: 14 }}>
          <input type="text" placeholder="Search employees…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {!list.length
          ? <EmptyState title="No employees found" sub={search ? "Nothing matches that search." : "No evaluations under this supervisor."} />
          : <>
              <SectionLabel>Employees ({list.length})</SectionLabel>
              <div style={grid210}>
                {list.map(e => <EmployeeCard key={e.name} emp={e} onClick={() => goForms(e.name)} />)}
              </div>
            </>
        }
      </>
    );
  };

  const renderForms = () => {
    const list = getForms(currentSup, currentEmp);
    return (
      <>
        <BackButton onClick={() => goEmployees(currentSup)} label="Back to Employees" />
        <Banner name={currentEmp} sub={`${currentSup} · ${list.length} weekly evaluation${list.length === 1 ? "" : "s"}`} />
        {!list.length
          ? <EmptyState title="No forms for this employee" sub="No weekly evaluations recorded." />
          : <>
              <SectionLabel>Weekly Forms ({list.length})</SectionLabel>
              <div style={grid250}>
                {list.map((r, i) => <FormCard key={r.evaluationId ?? i} record={r} onOpen={() => setOpenForm(r)} />)}
              </div>
            </>
        }
      </>
    );
  };

  return (
    <div className="nv-eval" style={{ background: T.bg, fontFamily: "'DM Sans', sans-serif", color: T.text, padding: "14px 18px", minHeight: "100vh" }}>
      <style>{GLOBAL}</style>

      {/* ─── Header ─── */}
      <div style={{ ...card, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ padding: "20px 24px 18px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 9, color: T.textMuted, letterSpacing: "0.18em", marginBottom: 6 }}>NOVAA · C&I MANAGEMENT</div>
            <div style={{ fontSize: 27, fontWeight: 800, color: T.textStrong, letterSpacing: "-0.03em", lineHeight: 1 }}>Employee Evaluation</div>
            <div style={{ fontSize: 12.5, color: T.textMuted, marginTop: 8 }}>
              {loading
                ? "Loading…"
                : <>Weekly performance assessments{lastRefresh && <> · updated {lastRefresh.toLocaleTimeString()}</>}</>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={handleDownloadCSV} disabled={loading || !db.length}
              style={{ padding: "9px 16px", background: "rgba(34,211,165,0.12)", border: `1px solid rgba(34,211,165,0.35)`, borderRadius: 8, color: T.green, cursor: (loading || !db.length) ? "not-allowed" : "pointer", fontSize: 11.5, fontWeight: 700, opacity: (loading || !db.length) ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif" }}>
              📥 Download CSV
            </button>
            <button onClick={fetchData} disabled={loading}
              style={{ padding: "9px 16px", background: "var(--nd-w05)", border: `1px solid ${T.border2}`, borderRadius: 8, color: T.text, cursor: loading ? "not-allowed" : "pointer", fontSize: 11.5, fontWeight: 700, opacity: loading ? 0.6 : 1, fontFamily: "'DM Sans', sans-serif" }}>
              {loading ? "Loading…" : "↻ Refresh"}
            </button>
          </div>
        </div>
        <div style={{ height: 2, background: "linear-gradient(90deg,#3B82F6,#22D3A5,transparent)" }} />
      </div>

      {/* ─── KPI tiles ─── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12, marginBottom: 12 }}>
        <StatCard value={allSups.length} label="Supervisors" loading={loading} />
        <StatCard value={allEmps.length} label="Employees"   loading={loading} />
        <StatCard value={db.length}      label="Total Forms" loading={loading} />
        <StatCard value={db.length ? avgScore : "—"} label="Avg Score" tone={db.length ? scoreTone(avgScore) : undefined} loading={loading} />
      </div>

      {/* ─── Breadcrumb ─── */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.textMuted, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={{ cursor: "pointer", color: view === "supervisors" ? T.textStrong : T.blueLight, fontWeight: view === "supervisors" ? 700 : 600 }} onClick={goSupervisors}>
          All Supervisors
        </span>
        {currentSup && (<>
          <span style={{ color: T.faint }}>›</span>
          {view === "employees"
            ? <span style={{ color: T.textStrong, fontWeight: 700 }}>{currentSup}</span>
            : <span style={{ cursor: "pointer", color: T.blueLight, fontWeight: 600 }} onClick={() => goEmployees(currentSup)}>{currentSup}</span>}
        </>)}
        {currentEmp && (<>
          <span style={{ color: T.faint }}>›</span>
          <span style={{ color: T.textStrong, fontWeight: 700 }}>{currentEmp}</span>
        </>)}
      </div>

      {view === "supervisors" && renderSupervisors()}
      {view === "employees"   && renderEmployees()}
      {view === "forms"       && renderForms()}

      {openForm && <FilledFormModal record={openForm} onClose={() => setOpenForm(null)} />}
    </div>
  );
}